'use client';

import { motion } from 'framer-motion';
import CmsImage from '@/components/ui/CmsImage';
import { stripHtml } from '@/lib/homepage-mappers';
import { MEDIA_PLACEHOLDER } from '@/lib/media-placeholder';
import { getStrapiOriginalMediaUrl } from '@/lib/strapi-media-url';
import type { AboutImage, AboutSectionHeading } from '@/types/aboutus';

const heroStagger = { hidden: {}, show: { transition: { staggerChildren: 0.12 } } };
const heroFadeUp = {
  hidden: { opacity: 0, y: 28 },
  show: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.7, ease: [0.22, 1, 0.36, 1] as [number, number, number, number] },
  },
};

function isAccentColor(color: string | null | undefined): boolean {
  if (!color) return false;
  const normalized = color.trim().toUpperCase();
  return normalized === '#EC1C24' || normalized === '#ED1C22' || normalized === '#E31E24';
}

interface CommunityHeroProps {
  bannerHeading: AboutSectionHeading[];
  bannerDescription?: string;
  bannerMedia?: AboutImage | null;
}

export default function CommunityHero({
  bannerHeading,
  bannerDescription,
  bannerMedia,
}: CommunityHeroProps) {
  const heroImage = getStrapiOriginalMediaUrl(bannerMedia) ?? '/images/community_img.png';
  const description = bannerDescription?.trim() ? stripHtml(bannerDescription) : '';
  const heroAlt = bannerMedia?.alternativeText || 'Community impact at Wonder Cement';

  const renderContent = (isMobile: boolean) => (
    <motion.div initial="hidden" animate="show" variants={heroStagger}>
      {bannerHeading.length > 0
        ? bannerHeading.map((part, index) => {
            const accent = isAccentColor(part.textColor);
            const muted = part.textColor?.trim().toUpperCase() === '#8D8D8D';
            let textColorClass: string;
            if (accent) {
              textColorClass = 'text-[#ed1c22]';
            } else if (muted) {
              textColorClass = 'text-white/50';
            } else {
              textColorClass = 'text-white';
            }
            return (
              <motion.h1
                key={part.id ?? index}
                variants={heroFadeUp}
                className={`hero-title ${textColorClass}`}
              >
                {part.text}
                {index < bannerHeading.length - 1 ? <br /> : null}
              </motion.h1>
            );
          })
        : null}

      {description ? (
        <motion.p
          variants={heroFadeUp}
          className={`hero-subtitle text-white/85 ${
            isMobile ? 'mt-4 max-w-3xl' : 'mt-5 max-w-md sm:mt-6'
          }`}
        >
          {description}
        </motion.p>
      ) : null}
    </motion.div>
  );

  return (
    <>
      {/* Mobile / Tablet: full-width image first, then content below. */}
      <div className="bg-black lg:hidden">
        <div className="relative w-full">
          <CmsImage
            src={heroImage}
            media={bannerMedia}
            alt={heroAlt}
            width={1600}
            height={1000}
            priority
            loading="eager"
            sizes="100vw"
            className="block h-auto w-full object-cover"
            posterSrc={MEDIA_PLACEHOLDER}
            fallbackSrc={MEDIA_PLACEHOLDER}
          />
        </div>
        <div className="px-4 py-8 sm:px-8 sm:py-10 md:py-12">
          <div className="max-w-[880px]">{renderContent(true)}</div>
        </div>
      </div>

      {/* Desktop: existing two-column banner. */}
      <section className="relative hidden min-h-screen w-full overflow-hidden bg-[#1a1a1a] lg:flex">
        <div
          className="pointer-events-none absolute inset-0 opacity-[0.06]"
          style={{
            backgroundImage: 'radial-gradient(circle, #ffffff 1px, transparent 1px)',
            backgroundSize: '28px 28px',
          }}
        />

        <div className="relative z-10 flex min-h-screen w-full items-center">
          <div className="container mx-auto grid w-full grid-cols-[1fr_420px] items-center gap-16 pb-14 pt-[var(--site-header-height)] xl:grid-cols-[1fr_480px]">
            {renderContent(false)}

            <motion.div
              className="flex items-center justify-center"
              initial={{ opacity: 0, y: 40, scale: 0.96 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              transition={{ duration: 1, delay: 0.3, ease: [0.22, 1, 0.36, 1] }}
            >
              <div
                className="relative w-full overflow-hidden rounded-[2rem] shadow-[0_40px_80px_-20px_rgba(0,0,0,0.7)]"
                style={{ height: 'min(70vh, 620px)' }}
              >
                <CmsImage
                  src={heroImage}
                  media={bannerMedia}
                  alt={heroAlt}
                  fill
                  className="object-cover object-center"
                  priority
                  loading="eager"
                  sizes="(max-width: 1280px) 0vw, 480px"
                  posterSrc={MEDIA_PLACEHOLDER}
                  fallbackSrc={MEDIA_PLACEHOLDER}
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent" />
              </div>
            </motion.div>
          </div>
        </div>
      </section>
    </>
  );
}
